import { useState, useEffect, useCallback } from "react";
import { ClientOverviewRepositoryImpl } from "../../data/repositories/ClientOverviewRepositoryImpl";
import { GetLastMonthExpenseInsightsUseCase } from "../../domain/use-cases/GetLastMonthExpenseInsightsUseCase";
import type { LastMonthExpenseInsightItem } from "../../domain/entities/LastMonthExpenseInsightsEntity";

const repository = new ClientOverviewRepositoryImpl();
const useCase = new GetLastMonthExpenseInsightsUseCase(repository);

export interface LastMonthExpenseInsightsData {
  insights: LastMonthExpenseInsightItem[];
  noInsights: boolean;
  monthLabel: string;
  message: string | null;
  loading: boolean;
  error: string | null;
}

export interface UseLastMonthExpenseInsightsOptions {
  /** When false, the insights API is not called. Set to true only after last-month expense has loaded. */
  enabled?: boolean;
}

export function useLastMonthExpenseInsights(
  options?: UseLastMonthExpenseInsightsOptions,
): LastMonthExpenseInsightsData {
  const enabled = options?.enabled ?? true;

  const [insights, setInsights] = useState<LastMonthExpenseInsightItem[]>([]);
  const [noInsights, setNoInsights] = useState(false);
  const [monthLabel, setMonthLabel] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInsights = useCallback(async () => {
    if (!enabled) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const entity = await useCase.execute();
      setInsights(entity.insights);
      setNoInsights(entity.noInsights);
      setMonthLabel(entity.monthLabel);
      setMessage(entity.message);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load last month expense insights",
      );
      setInsights([]);
      setNoInsights(true);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    fetchInsights();
  }, [enabled, fetchInsights]);

  return { insights, noInsights, monthLabel, message, loading, error };
}
